import type { GridFilterModel } from '@mui/x-data-grid-pro';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const FILTER_APPLY_DEBOUNCE_MS = 500;

export const useDebouncedFilterApply = (applyFilterModel: (filterModel: GridFilterModel) => void) => {
  const pendingApplyTimerReference = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const applyFilterModelReference = useRef(applyFilterModel);
  applyFilterModelReference.current = applyFilterModel;

  const { key: locationKey } = useLocation();
  const navigationType = useNavigationType();

  const cancelPendingApply = () => {
    if (pendingApplyTimerReference.current) {
      clearTimeout(pendingApplyTimerReference.current);
      pendingApplyTimerReference.current = undefined;
    }
  };

  useEffect(() => {
    if (navigationType === 'POP') {
      cancelPendingApply();
    }
  }, [locationKey, navigationType]);

  useEffect(() => cancelPendingApply, []);

  const applyNow = (filterModel: GridFilterModel) => {
    cancelPendingApply();
    applyFilterModelReference.current(filterModel);
  };

  const scheduleApply = (filterModel: GridFilterModel) => {
    cancelPendingApply();
    pendingApplyTimerReference.current = setTimeout(() => {
      pendingApplyTimerReference.current = undefined;
      applyFilterModelReference.current(filterModel);
    }, FILTER_APPLY_DEBOUNCE_MS);
  };

  return { applyNow, scheduleApply };
};
